import ListAlt from "@mui/icons-material/ListAlt";
import { MenuItem, Stack } from "@mui/material";
import axios from "axios";
import { Activity, useEffect, useMemo, useRef, useState } from "react";

import CoAppToggle from "@/components/global/components/inputs/CoAppToggle";
import type { ReactionResourceOption } from "@/components/global/components/tiptap/extensions/suggestion/suggestion-types";

import apiRoutes from "../../../constants/api-routes";
import useAuthHeader from "../../../hooks/useAuthHeader";
import {
	isFreeResponseReactionType,
	isUserConfirmationPopupReactionType,
	type Option,
} from "../constants";
import type {
	DatabaseTableOptionV2,
	ResourceCategoryV2,
	ScreenLocationFieldOption,
	VariableGroupV2,
	VisibleGroupKey,
} from "../fields/form-entity-select";
import FormEntitySelect from "../fields/form-entity-select";
import type { ScreenLocationOption } from "../modals/CreateRuleModal";
import { ReactionTextArea, type ReactionTextAreaHandle } from "../fields/ReactionTextArea";
import { getUniqueDefaultName } from "../nodes/nodeFactory";
import type { ReactionRuleNode } from "../types/nodes";
import { DUPLICATE_REACTION_ASSIGNMENT_DECISION_NAME_MESSAGE } from "../utils/node-name-uniqueness";
import { getEditorInsertLabelForPickedResource } from "../utils/resource-insert-label-utils";
import BaseForm from "./BaseForm";
import { CanvasFormTextField } from "./styled/CanvasFormTextField.styled";

export interface ReactionFormState {
	reactionName: string;
	reactionTypeId: string;
	reactionTypeName: string;
	reactionTypeFriendlyName: string;
	resource: string;
	reactionText: string;
	screenFieldId: string;
	screenFieldName: string;
	/** String form of {@link ReactionNodeData.minimumCharacterCount} (freeresponse only). */
	minimumCharacterCount: string;
	userConfirmationText: string;
	showConfirmationText: boolean;
	caseSensitive: boolean;
}

export interface ReactionFormProps {
	open: boolean;
	onClose: () => void;
	onSave: (state: ReactionFormState) => void;
	initialState?: Partial<ReactionFormState>;
	isSaving?: boolean;
	reactionTypeOptions?: Option[];
	patientFieldOptions?: Option[];
	/** When set, shows an inline error if another node of the same type already uses this name. */
	getDuplicateNameError?: (name: string) => string | undefined;
	/**
	 * Lowercased names of every reaction, assignment, and decision in the rule graph (excluding the
	 * active node's own name when editing). Mirrors {@link LoopFormProps.takenLoopNames} for default
	 * names and duplicate detection.
	 */
	takenReactionAssignmentDecisionNames?: Set<string>;
	/** Same resource wiring as DecisionForm Resource (global constants excluded). */
	platformId?: string | number;
	resourceVariableGroups?: VariableGroupV2[];
	formulaVariableOptions?: Option[];
	singleRowDatabaseVariableOptions?: Option[];
	resourceScreenFields?: Option[];
	resourceAdvancedTriggers?: Option[];
	/** Platform screen locations (same list as Create Rule modal). */
	screenLocationOptions?: ScreenLocationOption[];
	/** Rule's configured screen location — excluded from "Other Screens with Fields". */
	ruleScreenLocationId?: string | number;
	/** Writes API reaction type id/name onto canvas node before Save (manager approval discovery, etc.). */
	onReactionDraftPatch?: (
		patch: Pick<ReactionRuleNode["data"], "reactionTypeId" | "reactionTypeName" | "reactionTypeFriendlyName">,
	) => void;
}

const EMPTY_OPTIONS: Option[] = [];
const EMPTY_VARIABLE_GROUPS: VariableGroupV2[] = [];

function dedupeReactionResourceOptions(options: ReactionResourceOption[]): ReactionResourceOption[] {
	const byKey = new Map<string, ReactionResourceOption>();
	for (const o of options) {
		byKey.set(`${o.resourceType}:${o.value}`, o);
	}
	return [...byKey.values()];
}

const ReactionForm = (props: ReactionFormProps) => {
	const {
		open,
		onClose,
		onSave,
		initialState,
		isSaving = false,
		reactionTypeOptions,
		patientFieldOptions,
		getDuplicateNameError,
		takenReactionAssignmentDecisionNames,
		platformId,
		resourceVariableGroups = EMPTY_VARIABLE_GROUPS,
		formulaVariableOptions = EMPTY_OPTIONS,
		singleRowDatabaseVariableOptions = EMPTY_OPTIONS,
		resourceScreenFields = EMPTY_OPTIONS,
		resourceAdvancedTriggers = EMPTY_OPTIONS,
		screenLocationOptions = [],
		ruleScreenLocationId,
		onReactionDraftPatch,
	} = props;

	const authHeader = useAuthHeader();

	const normalizedReactionTypeOptions = useMemo<Option[]>(
		() => reactionTypeOptions ?? [],
		[reactionTypeOptions]
	);

	const normalizedPatientFieldOptions = useMemo<Option[]>(
		() => patientFieldOptions ?? [],
		[patientFieldOptions]
	);

	const [fetchedScreenFields, setFetchedScreenFields] = useState<Option[]>([]);
	const [fetchedDatabaseTables, setFetchedDatabaseTables] = useState<DatabaseTableOptionV2[]>([]);
	const [pickedResourceOptions, setPickedResourceOptions] = useState<ReactionResourceOption[]>([]);

	useEffect(() => {
		if (!open || !platformId) return;

		let cancelled = false;

		const fetchResourceOptions = async () => {
			try {
				const tablesResponse = await axios.get(apiRoutes.getTableOptions(platformId), { headers: authHeader });
				if (cancelled) return;
				const rawGroups = tablesResponse.data?.tableGroups ?? [];

				const screenTables: { id: number; name: string }[] = [];
				const databaseTables: { id: number; name: string }[] = [];

				for (const group of rawGroups) {
					if (group.screenTables) screenTables.push(...group.screenTables);
					if (group.databaseTables) databaseTables.push(...group.databaseTables);
				}

				const screenFieldResults = await Promise.all(
					screenTables.map(async (table) => {
						try {
							const res = await axios.get(apiRoutes.getScreenTableFields(table.id), { headers: authHeader });
							return (res.data ?? []).map((field: { id: number; name: string }) => ({
								label: `${table.name}.${field.name}`,
								value: String(field.id),
							}));
						} catch {
							return [];
						}
					}),
				);
				if (cancelled) return;
				setFetchedScreenFields(screenFieldResults.flat());

				const dbTableResults = await Promise.all(
					databaseTables.map(async (table) => {
						try {
							const res = await axios.get(apiRoutes.getDatabaseTableFields(table.id), { headers: authHeader });
							const fields: Option[] = (res.data ?? []).map((field: { id: number; name: string }) => ({
								label: field.name,
								value: String(field.id),
							}));
							return { label: table.name, value: String(table.id), fields };
						} catch {
							return { label: table.name, value: String(table.id), fields: [] };
						}
					}),
				);
				if (cancelled) return;
				setFetchedDatabaseTables(dbTableResults);
			} catch (error) {
				console.error("Error fetching resource options:", error);
			}
		};

		fetchResourceOptions();
		return () => { cancelled = true; };
	}, [open, platformId, authHeader]);

	const mergedScreenFields = useMemo(
		() => (resourceScreenFields.length ? resourceScreenFields : fetchedScreenFields),
		[resourceScreenFields, fetchedScreenFields],
	);

	const mergedDatabaseTables = fetchedDatabaseTables;

	const otherScreenLocations = useMemo((): ScreenLocationFieldOption[] => {
		if (!screenLocationOptions.length) return [];
		return screenLocationOptions
			.filter((loc) => String(loc.id) !== String(ruleScreenLocationId ?? ""))
			.map((loc) => ({ id: loc.id, name: loc.name }));
	}, [screenLocationOptions, ruleScreenLocationId]);

	const ruleScreenLocationName = useMemo(() => {
		const match = screenLocationOptions.find(
			(loc) => String(loc.id) === String(ruleScreenLocationId ?? ""),
		);
		return match?.name;
	}, [screenLocationOptions, ruleScreenLocationId]);

	const resourceVisibleGroups = useMemo((): VisibleGroupKey[] => {
		const keys: VisibleGroupKey[] = [];
		if (mergedScreenFields.length) keys.push("screenFields");
		if (resourceAdvancedTriggers.length) keys.push("advancedTriggers");
		const hasSimpleVariables = resourceVariableGroups.some((g) => g.options.length > 0);
		if (hasSimpleVariables) keys.push("simpleVariable");
		if (formulaVariableOptions.length) keys.push("formulaVariable");
		if (singleRowDatabaseVariableOptions.length) keys.push("singleRowDatabaseVariables");
		if (otherScreenLocations.length) keys.push("otherScreenLocations");
		return keys;
	}, [
		mergedScreenFields,
		resourceAdvancedTriggers.length,
		resourceVariableGroups,
		formulaVariableOptions.length,
		singleRowDatabaseVariableOptions.length,
		otherScreenLocations.length,
	]);

	const baseAvailableResourcesForEditor = useMemo((): ReactionResourceOption[] => {
		const fromSimple = resourceVariableGroups.flatMap((g) =>
			g.options.map((o) => ({ ...o, resourceType: "simpleVariable" as const })),
		);
		const fromDbTables = mergedDatabaseTables.flatMap((t) =>
			t.fields.map((o) => ({ ...o, resourceType: "databaseTableField" as const })),
		);
		return [
			...fromSimple,
			...formulaVariableOptions.map((o) => ({ ...o, resourceType: "formulaVariable" as const })),
			...singleRowDatabaseVariableOptions.map((o) => ({
				...o,
				resourceType: "databaseSingleRowVariable" as const,
			})),
			...mergedScreenFields.map((o) => ({ ...o, resourceType: "screenField" as const })),
			...resourceAdvancedTriggers.map((o) => ({
				label: o.label,
				value: o.value,
				triggerName: o.triggerName,
				resourceType: "advancedTrigger" as const,
			})),
			...fromDbTables,
		];
	}, [
		resourceVariableGroups,
		formulaVariableOptions,
		singleRowDatabaseVariableOptions,
		mergedScreenFields,
		resourceAdvancedTriggers,
		mergedDatabaseTables,
	]);

	const availableResourcesForEditor = useMemo(
		() => dedupeReactionResourceOptions([...baseAvailableResourcesForEditor, ...pickedResourceOptions]),
		[baseAvailableResourcesForEditor, pickedResourceOptions],
	);

	const buildInitialState = (): ReactionFormState => {
		const reactionTypeId = initialState?.reactionTypeId ?? normalizedReactionTypeOptions[0]?.value ?? "";
		const optForId = normalizedReactionTypeOptions.find((o) => o.value === reactionTypeId);
		const reactionTypeName =
			optForId?.reactionTypeApiName ?? initialState?.reactionTypeName ?? "";
		const reactionTypeFriendlyName =
			optForId?.label ?? initialState?.reactionTypeFriendlyName ?? "";
		const screenFieldId = initialState?.screenFieldId ?? "";
		const screenFieldName = initialState?.screenFieldName
			?? normalizedPatientFieldOptions.find((o) => o.value === screenFieldId)?.label
			?? "";
		return {
			reactionName: initialState?.reactionName
				? initialState.reactionName
				: takenReactionAssignmentDecisionNames
					? getUniqueDefaultName("Reaction", takenReactionAssignmentDecisionNames, true)
					: "",
			reactionTypeId,
			reactionTypeName,
			reactionTypeFriendlyName,
			resource: initialState?.resource ?? "",
			screenFieldId,
			screenFieldName,
			reactionText: initialState?.reactionText ?? "Patient cannot have deceased in the name.",
			minimumCharacterCount:
				initialState?.minimumCharacterCount != null && initialState.minimumCharacterCount !== ""
					? String(initialState.minimumCharacterCount)
					: "",
			userConfirmationText: initialState?.userConfirmationText ?? "",
			showConfirmationText: initialState?.showConfirmationText ?? true,
			caseSensitive: initialState?.caseSensitive ?? false,
		};
	};

	const initialFormState = useMemo(buildInitialState, [
		initialState,
		normalizedPatientFieldOptions,
		normalizedReactionTypeOptions,
		takenReactionAssignmentDecisionNames,
	]);

	const [formState, setFormState] = useState<ReactionFormState>(initialFormState);
	const textAreaRef = useRef<ReactionTextAreaHandle>(null);

	useEffect(() => {
		if (open) {
			setFormState(initialFormState);
			setPickedResourceOptions([]);
		}
	}, [initialFormState, open]);

	useEffect(() => {
		if (!open || !onReactionDraftPatch) return;
		const opt = normalizedReactionTypeOptions.find((o) => o.value === initialFormState.reactionTypeId);
		const apiName = opt?.reactionTypeApiName ?? "";
		const friendlyName = opt?.label ?? "";
		if (!initialFormState.reactionTypeId || !apiName) return;
		if (
			initialState?.reactionTypeId === initialFormState.reactionTypeId &&
			(initialState?.reactionTypeName ?? "") === apiName &&
			(initialState?.reactionTypeFriendlyName ?? "") === friendlyName
		) {
			return;
		}
		onReactionDraftPatch({
			reactionTypeId: initialFormState.reactionTypeId,
			reactionTypeName: apiName,
			reactionTypeFriendlyName: friendlyName,
		});
	}, [
		open,
		initialFormState.reactionTypeId,
		normalizedReactionTypeOptions,
		onReactionDraftPatch,
		initialState?.reactionTypeId,
		initialState?.reactionTypeName,
		initialState?.reactionTypeFriendlyName,
	]);

	const selectedReactionTypeOption = useMemo(
		() => normalizedReactionTypeOptions.find((o) => o.value === formState.reactionTypeId),
		[normalizedReactionTypeOptions, formState.reactionTypeId],
	);

	const inlineOrDisableNeedsScreenField = selectedReactionTypeOption
		? ["Inline", "Disable"].includes(selectedReactionTypeOption.label)
		: false;

	const isFreeResponseReaction = isFreeResponseReactionType(formState.reactionTypeName);
	const isUserConfirmationPopupReaction = isUserConfirmationPopupReactionType(formState.reactionTypeName);

	const plainText = formState.reactionText.replace(/<[^>]*>/g, "").trim();
	const hasResourceToken = /\{[^{}]+\}/.test(formState.reactionText);
	const hasContent = Boolean(plainText) || hasResourceToken;

	const minimumCharacterCountTrimmed = formState.minimumCharacterCount.trim();
	const minimumCharacterCountValid =
		/^\d+$/.test(minimumCharacterCountTrimmed) && Number(minimumCharacterCountTrimmed) >= 1;

	const duplicateNameError = useMemo((): string | undefined => {
		const trimmed = formState.reactionName.trim();
		const trimmedLower = trimmed.toLowerCase();
		if (!trimmed) return undefined;
		const fromProp = getDuplicateNameError?.(trimmed);
		if (fromProp) return fromProp;
		if (
			takenReactionAssignmentDecisionNames &&
			initialState?.reactionName?.trim().toLowerCase() !== trimmedLower &&
			takenReactionAssignmentDecisionNames.has(trimmedLower)
		) {
			return DUPLICATE_REACTION_ASSIGNMENT_DECISION_NAME_MESSAGE;
		}
		return undefined;
	}, [
		getDuplicateNameError,
		takenReactionAssignmentDecisionNames,
		formState.reactionName,
		initialState?.reactionName,
	]);

	const isSaveDisabled =
		!formState.reactionName ||
		Boolean(duplicateNameError) ||
		!formState.reactionTypeId ||
		(inlineOrDisableNeedsScreenField && !formState.screenFieldId) ||
		(isFreeResponseReaction && !minimumCharacterCountValid) ||
		!hasContent;

	const handleSave = () => {
		onSave(formState);
	};

	const handleInsertResourceChange = (
		val: string,
		category: ResourceCategoryV2,
		label: string,
		_dataType?: string,
		locationName?: string,
	) => {
		if (category === "globalConstant") return;
		const insertLabel = category === "screenField"
			? getEditorInsertLabelForPickedResource(
					category,
					label,
					val,
					mergedScreenFields,
					resourceAdvancedTriggers,
					locationName,
					ruleScreenLocationName,
				)
			: label;
		const advancedTriggerMatch =
			category === "advancedTrigger"
				? resourceAdvancedTriggers.find((t) => t.value === val)
				: undefined;
		const resource: ReactionResourceOption = {
			label: insertLabel,
			value: val,
			resourceType: category,
			...(advancedTriggerMatch?.triggerName
				? { triggerName: advancedTriggerMatch.triggerName }
				: {}),
		};
		setPickedResourceOptions((previous) => dedupeReactionResourceOptions([...previous, resource]));
		textAreaRef.current?.insertAtCursor(resource);
		setFormState((previous) => ({ ...previous, resource: "" }));
	};
	
	return (
		<BaseForm
			open={open}
			onClose={onClose}
			onSave={handleSave}
			title="Reaction"
			icon={<ListAlt />}
			iconColor="#D32E26"
			gradient="linear-gradient(135deg, #E94136 0%, #B81E15 100%)"
			isSaving={isSaving}
			isSaveDisabled={isSaveDisabled}
			saveButtonText="Save Reaction"
			width={520}
		>
			<Stack spacing={3} sx={{ p: 2 }}>
				<CanvasFormTextField 
					fullWidth
					label="Reaction Name" 
					value={formState.reactionName} 
					width="100%"
					error={Boolean(duplicateNameError)}
					helperText={duplicateNameError ?? ""}
					onChange={(event) => setFormState((previous) => ({
						...previous,
						reactionName: event.target.value
					}))} 
				/>
			</Stack>
			<Stack sx={{ p: 2, justifyContent: "flex-start", gap: 3 }} direction="row">
				<CanvasFormTextField
					select
					fullWidth
					label="Type of Reaction"
					width="100%"
					value={formState.reactionTypeId}
					onChange={(event) => {
						const newTypeId = event.target.value;
						const selectedOption = normalizedReactionTypeOptions.find((o) => o.value === newTypeId);
						const apiName = selectedOption?.reactionTypeApiName ?? "";
						const friendlyName = selectedOption?.label ?? "";
						const fieldApplies = selectedOption ? ["Inline", "Disable"].includes(selectedOption.label) : false;
						setFormState((previous) => ({
							...previous,
							reactionTypeId: newTypeId,
							reactionTypeName: apiName,
							reactionTypeFriendlyName: friendlyName,
							screenFieldId: fieldApplies ? previous.screenFieldId : "",
							screenFieldName: fieldApplies ? previous.screenFieldName : ""
						}));
						if (newTypeId && apiName) {
							onReactionDraftPatch?.({
								reactionTypeId: newTypeId,
								reactionTypeName: apiName,
								reactionTypeFriendlyName: friendlyName,
							});
						}
					}}
				>
					{normalizedReactionTypeOptions.map((option) => (
						<MenuItem key={option.value} value={option.value}>
							{option.label}
						</MenuItem>
					))}
				</CanvasFormTextField>
				<CanvasFormTextField
					select
					fullWidth
					label="Fields"
					disabled={!inlineOrDisableNeedsScreenField}
					value={formState.screenFieldName}
					width="100%"
					onChange={(event) => {
						const newFieldName = event.target.value;
						const selectedField = normalizedPatientFieldOptions.find((o) => o.label === newFieldName);
						setFormState((previous) => ({
							...previous,
							screenFieldId: selectedField?.value ?? "",
							screenFieldName: selectedField?.label ?? ""
						}));
					}}
				>
					{normalizedPatientFieldOptions.map((option) => (
						<MenuItem key={option.value} value={option.label}>
							{option.label}
						</MenuItem>
					))}
				</CanvasFormTextField>
			</Stack>
			<Stack spacing={3} sx={{ p: 2 }}>

				<FormEntitySelect
					label="Insert Resource"
					value={formState.resource}
					ruleScreenLocationName={ruleScreenLocationName}
					onChange={(
						val: string,
						category: ResourceCategoryV2,
						label: string,
						dataType?: string,
						locationName?: string,
					) => {
						handleInsertResourceChange(val, category, label, dataType, locationName);
					}}
					variableGroups={resourceVariableGroups}
					formulaVariables={formulaVariableOptions}
					databaseSingleRowVariableOptions={singleRowDatabaseVariableOptions}
					screenFields={mergedScreenFields}
					advancedTriggers={resourceAdvancedTriggers}
					otherScreenLocations={otherScreenLocations}
					visibleGroups={resourceVisibleGroups}
				/>
				<ReactionTextArea
					ref={textAreaRef}
					value={formState.reactionText}
					onChange={(html) => setFormState((previous) => ({
						...previous,
						reactionText: html
					}))}
					availableResources={availableResourcesForEditor}
				/>
				<Activity mode={isFreeResponseReaction ? "visible" : "hidden"}>
					<CanvasFormTextField
						fullWidth
						type="number"
						label="Minimum Character Count"
						width="100%"
						value={formState.minimumCharacterCount}
						slotProps={{ htmlInput: { min: 1, step: 1 } }}
						onChange={(event) => setFormState((previous) => ({
							...previous,
							minimumCharacterCount: event.target.value,
						}))}
					/>
				</Activity>
				<Activity mode={isUserConfirmationPopupReaction ? "visible" : "hidden"}>
					<Stack spacing={2}>
						<CanvasFormTextField
							fullWidth
							label="User Confirmation Text"
							width="100%"
							value={formState.userConfirmationText}
							onChange={(event) => setFormState((previous) => ({
								...previous,
								userConfirmationText: event.target.value,
							}))}
						/>
						<CoAppToggle
							label="Show Confirmation Text"
							checked={formState.showConfirmationText}
							onChange={(checked) => setFormState((previous) => ({
								...previous,
								showConfirmationText: checked,
							}))}
						/>
						<CoAppToggle
							label="Case Sensitive"
							checked={formState.caseSensitive}
							onChange={(checked) => setFormState((previous) => ({
								...previous,
								caseSensitive: checked,
							}))}
						/>
					</Stack>
				</Activity>
			</Stack>
		</BaseForm>
	);
};

export default ReactionForm;

